// Forgot password — enter your email, get a reset link. Always shows the same
// "check your email" confirmation (no account enumeration).

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Mail, ArrowLeft, MailCheck } from 'lucide-react';
import { toast } from 'sonner';
import { auth } from '../../lib/api';

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try { await auth.forgotPassword(email.trim()); setSent(true); }
    catch (err) { toast.error((err as Error).message || 'Something went wrong'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
        <div className="text-[20px] font-bold text-brand-700 text-center">Lakay<span className="text-amber-500">Cart</span></div>
        {sent ? (
          <div className="text-center mt-5">
            <MailCheck className="w-9 h-9 text-emerald-500 mx-auto" />
            <p className="mt-3 text-[15px] font-semibold text-slate-900">Check your email</p>
            <p className="mt-1 text-[12.5px] text-slate-500">If an account exists for <b>{email.trim()}</b>, we've sent a reset link. It expires in 1 hour.</p>
            <Link to="/login" className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-brand-600 hover:underline"><ArrowLeft className="w-3.5 h-3.5" /> Back to sign in</Link>
          </div>
        ) : (
          <>
            <p className="mt-4 text-[14px] font-semibold text-slate-900 text-center">Reset your password</p>
            <p className="mt-1 text-[12.5px] text-slate-500 text-center">Enter your email and we'll send you a reset link.</p>
            <form onSubmit={submit} className="mt-5 space-y-3">
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Email</span>
                <div className="mt-1 relative">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required placeholder="name@email.com"
                    className="w-full pl-9 pr-3 py-2.5 text-[14px] border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
                </div>
              </label>
              <button type="submit" disabled={busy || !email.trim()} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-brand-600 text-white text-[14px] font-semibold hover:bg-brand-700 disabled:opacity-50">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Send reset link
              </button>
            </form>
            <Link to="/login" className="mt-4 inline-flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-slate-900"><ArrowLeft className="w-3.5 h-3.5" /> Back to sign in</Link>
          </>
        )}
      </div>
    </div>
  );
}
